// components/charts/BarChart.tsx
import React, { useMemo, useCallback } from "react";
import {
  ResponsiveContainer,
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";

interface BarChartProps {
  title: string;
  description?: string;
  data: any[];
  xAxisKey: string;
  yAxisKey: string;
  barColor?: string;
  showGrid?: boolean;
  height?: number;
  loading?: boolean;
  error?: string;
  barRadius?: number;
  gradient?: boolean;
}

const BarChart: React.FC<BarChartProps> = ({
  title,
  description,
  data,
  xAxisKey,
  yAxisKey,
  barColor = "#3b82f6",
  showGrid = true,
  height = 400,
  loading = false,
  error,
  barRadius = 4,
  gradient = false,
}) => {
  const gradientId = useMemo(
    () => `barGradient-${Math.random().toString(36).substr(2, 9)}`,
    []
  );

  const CustomTooltip = useCallback(
    ({ active, payload, label }: any) => {
      if (!active || !payload?.length) return null;

      return (
        <div className="custom-tooltip">
          <p className="tooltip-title">{label}</p>
          <div className="tooltip-item">
            <div className="tooltip-label">
              <div
                className="tooltip-dot"
                style={{ backgroundColor: barColor }}
              />
              <span>{payload[0].name || yAxisKey}</span>
            </div>
            <span className="tooltip-value">
              {typeof payload[0].value === "number"
                ? payload[0].value.toLocaleString()
                : payload[0].value}
            </span>
          </div>
        </div>
      );
    },
    [barColor, yAxisKey]
  );

  const chartElements = useMemo(() => {
    if (!data?.length) return null;

    return (
      <>
        <defs>
          {gradient && (
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={barColor} stopOpacity={0.8} />
              <stop offset="95%" stopColor={barColor} stopOpacity={0.3} />
            </linearGradient>
          )}
        </defs>

        {showGrid && (
          <CartesianGrid
            strokeDasharray="2 4"
            stroke="#d1d5db"
            horizontal
            vertical
          />
        )}

        <XAxis
          dataKey={xAxisKey}
          axisLine={false}
          tickLine={false}
          tick={{ fontSize: 12, fill: "#6b7280" }}
        />

        <YAxis
          axisLine={false}
          tickLine={false}
          tick={{ fontSize: 12, fill: "#6b7280" }}
        />

        <Tooltip
          content={<CustomTooltip />}
          cursor={{ fill: "rgba(0,0,0,0.1)" }}
        />

        <Bar
          dataKey={yAxisKey}
          fill={gradient ? `url(#${gradientId})` : barColor}
          radius={[barRadius, barRadius, 0, 0]}
        />
      </>
    );
  }, [
    data,
    xAxisKey,
    yAxisKey,
    barColor,
    showGrid,
    gradient,
    gradientId,
    barRadius,
    CustomTooltip,
  ]);

  const chartStates = useMemo(
    () => ({
      loading: (
        <div className="chart-loading">
          <i className="ti ti-loader-2 spin" />
          <span>Loading...</span>
        </div>
      ),
      error: (
        <div className="chart-error">
          <i className="ti ti-alert-circle" />
          <span>{error}</span>
        </div>
      ),
      empty: (
        <div className="chart-empty">
          <i className="ti ti-chart-bar" />
          <span>No data available</span>
        </div>
      ),
    }),
    [error]
  );

  const renderChart = () => {
    if (loading) return chartStates.loading;
    if (error) return chartStates.error;
    if (!data?.length) return chartStates.empty;

    return (
      <RechartsBarChart
        data={data}
        margin={{ top: 10, right: 30, left: 20, bottom: 5 }}>
        {chartElements}
      </RechartsBarChart>
    );
  };

  return (
    <div className="bar-chart-container">
      <div className="chart-wrapper" style={{ height: `${height}px` }}>
        <ResponsiveContainer width="100%" height="100%">
          {renderChart()}
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default BarChart;
